import { useEffect, useState } from "react";
import api, { price, formatDate } from "../lib/api";
import DateRange, { rangeParams } from "../components/DateRange";
import { ArrowUpRight, BellOff, Target, TrendingUp } from "lucide-react";

const norm = (s) => (s || "").trim().toLowerCase().replace(/\s+/g, " ");

export default function Alerts() {
  const [alerts, setAlerts] = useState(null);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [catMap, setCatMap] = useState({});
  const [catColors, setCatColors] = useState({});
  const [cats, setCats] = useState([]);
  const [filterCat, setFilterCat] = useState("");

  useEffect(() => {
    api.get("/alerts", { params: rangeParams(from, to) }).then(({ data }) => setAlerts(data));
  }, [from, to]);
  useEffect(() => {
    api.get("/catalog/products").then(({ data }) => setCatMap(Object.fromEntries(data.map((p) => [p.normalized_name, p.category])))).catch(() => {});
    api.get("/categories/colors").then(({ data }) => setCatColors(data)).catch(() => {});
    api.get("/categories").then(({ data }) => setCats(data)).catch(() => {});
  }, []);

  const shown = (alerts || []).filter((a) => !filterCat || catMap[norm(a.product)] === filterCat);

  return (
    <div className="space-y-8">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.15em] font-bold text-stone-500">Monitoraggio</p>
          <h1 className="font-serif text-4xl sm:text-5xl font-light tracking-tight text-stone-900">Avvisi prezzi</h1>
          <p className="text-sm text-stone-500 mt-2">Aumenti rispetto all'acquisto precedente e prodotti che superano il prezzo obiettivo.</p>
        </div>
        <DateRange from={from} to={to} onFrom={setFrom} onTo={setTo} onClear={() => { setFrom(""); setTo(""); }} />
      </div>

      {alerts && alerts.length > 0 && (
        <div data-testid="reparto-filter" className="flex flex-wrap gap-2">
          <button data-testid="filter-all" onClick={() => setFilterCat("")} className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors ${!filterCat ? "bg-brand text-white border-brand" : "bg-white border-stone-300 text-stone-600 hover:bg-stone-50"}`}>Tutti</button>
          {cats.map((c) => (
            <button key={c} data-testid={`filter-${c}`} onClick={() => setFilterCat(filterCat === c ? "" : c)}
              className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors inline-flex items-center gap-1.5 ${filterCat === c ? "text-white border-transparent" : "bg-white border-stone-300 text-stone-600 hover:bg-stone-50"}`}
              style={filterCat === c ? { background: catColors[c] || "#2C4A3B" } : undefined}>
              <span className="w-2 h-2 rounded-full" style={{ background: catColors[c] || "#A8A29E" }} />{c}
            </button>
          ))}
        </div>
      )}

      {alerts && alerts.length === 0 && (
        <div className="bg-white border border-stone-200 rounded-sm p-16 text-center text-stone-400">
          <BellOff size={32} strokeWidth={1.3} className="mx-auto mb-3" />
          <p className="font-serif text-2xl text-stone-600">Nessun avviso</p>
          <p className="text-sm mt-1">I prezzi dei tuoi fornitori sono nei limiti. Ottimo!</p>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {shown.map((a, i) => {
          const isTarget = a.type === "target";
          return (
            <div key={`${a.type}-${a.product}-${a.supplier}-${a.current_date}-${i}`} data-testid={`alert-card-${i}`}
              className={`bg-white border border-stone-200 rounded-sm p-6 animate-fade-in-up border-l-4 ${isTarget ? "border-l-brand-accent" : "border-l-alert"}`}>
              <div className="flex items-center gap-2 mb-3">
                {isTarget ? <Target size={14} className="text-brand-accent" /> : <TrendingUp size={14} className="text-alert" />}
                <span className={`text-xs uppercase tracking-[0.12em] font-bold ${isTarget ? "text-brand-accent" : "text-alert"}`}>
                  {isTarget ? "Supera obiettivo" : "Aumento prezzo"}
                </span>
              </div>
              <div className="flex items-start justify-between mb-4">
                <div className="min-w-0">
                  <h3 className="font-serif text-2xl text-stone-900 truncate flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: catColors[catMap[norm(a.product)]] || "#D6D3D1" }} />{a.product}</h3>
                  <p className="text-sm text-stone-500 truncate">{a.supplier}</p>
                </div>
                <span className="flex items-center gap-1 text-alert font-bold shrink-0">
                  <ArrowUpRight size={18} /> +{a.change_pct}%
                </span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <div>
                  <p className="text-xs text-stone-400">{isTarget ? "Obiettivo" : formatDate(a.previous_date)}</p>
                  <p className="text-stone-500 line-through">{price(isTarget ? a.target_price : a.previous_price)}</p>
                </div>
                <div className="h-px flex-1 mx-3 bg-stone-200" />
                <div className="text-right">
                  <p className="text-xs text-stone-400">{formatDate(a.current_date)}</p>
                  <p className="font-bold text-alert">{price(a.current_price)}</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
